import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    X, Send, Sparkles, Bot, User, FileText, Brain, CheckSquare, BookOpen,
    Save, Clock, Loader, ChevronRight, ChevronLeft, Lightbulb, RefreshCw, Trash2,
    Settings, Users, Calendar, Paperclip, AlertCircle, Check, Upload, Globe, File,
    GripVertical, Edit3, ChevronDown, ChevronUp, ExternalLink, Shield, Layers, Loader2, StopCircle,
    PanelLeftClose, PanelLeftOpen, MessageSquare, Eye
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import QuizBuilder from './QuizBuilder';
import TrainingBuilder from './TrainingBuilder';
import ChecklistBuilder from './ChecklistBuilder';
import CombinedTaskBuilder from './CombinedTaskBuilder';
import EmployeeSelector from './EmployeeSelector';
import UnifiedFilePickerModal from '../Common/UnifiedFilePickerModal';
import AdminTaskPreviewModal from './AdminTaskPreviewModal';

// Web Search Results Display Component - Collapsible cards with View More
const WebSearchResultsDisplay = ({ results }) => {
    const [expandedIndex, setExpandedIndex] = useState(null);

    if (!results || results.length === 0) return null;

    return (
        <div className="mt-3 pt-3 border-t border-gray-300/50 dark:border-slate-700/30">
            <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                <span className="text-xs font-medium text-gray-500 dark:text-slate-400">Web Sources</span>
            </div>
            <div className="space-y-2">
                {results.map((result, index) => (
                    <div
                        key={index}
                        className="bg-gray-200/60 dark:bg-slate-800/40 border border-gray-300/60 dark:border-slate-700/50 rounded-lg overflow-hidden"
                    >
                        {/* Header - Always visible */}
                        <div className="px-3 py-2">
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <a
                                        href={result.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 truncate block"
                                    >
                                        {result.title}
                                    </a>
                                    <span className="text-xs text-gray-500 dark:text-slate-500">{result.domain}</span>
                                </div>
                                <a
                                    href={result.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1 text-gray-400 dark:text-slate-500 hover:text-blue-500 dark:hover:text-blue-400 transition-colors flex-shrink-0"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                            </div>
                            {/* Preview snippet - 2-3 lines */}
                            <p className="text-xs text-gray-600 dark:text-slate-400 mt-1 line-clamp-2">
                                {result.snippet}
                            </p>
                        </div>
                        {/* Expandable full content */}
                        {result.fullContent && (
                            <>
                                {expandedIndex === index && (
                                    <div className="px-3 pb-2">
                                        <div className="text-xs text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-900/50 p-2 rounded max-h-40 overflow-y-auto">
                                            {result.fullContent}
                                        </div>
                                    </div>
                                )}
                                <button
                                    onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                                    className="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 bg-gray-200/60 dark:bg-slate-800/60 hover:bg-gray-300/50 dark:hover:bg-slate-700/50 transition-colors"
                                >
                                    {expandedIndex === index ? (
                                        <>
                                            <ChevronUp className="w-3 h-3" />
                                            <span>Show Less</span>
                                        </>
                                    ) : (
                                        <>
                                            <ChevronDown className="w-3 h-3" />
                                            <span>View More</span>
                                        </>
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const AITaskGenerator = ({ isOpen, onClose, onSuccess, phase, editTask = null, initialMessages = null, isComplianceMode = false }) => {
    // Determine if we're in edit mode
    const isEditMode = editTask !== null && editTask?.id;

    // Chat State
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const chatEndRef = useRef(null);
    const inputRef = useRef(null);

    // Mobile State - 'chat' or 'editor' panel
    const [mobileActivePanel, setMobileActivePanel] = useState('chat');

    // Chat panel collapse state (desktop only)
    const [isChatCollapsed, setIsChatCollapsed] = useState(() => {
        const saved = localStorage.getItem('ai_task_generator_chat_collapsed');
        return saved === 'true';
    });

    const toggleChatCollapsed = useCallback(() => {
        setIsChatCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('ai_task_generator_chat_collapsed', String(next));
            return next;
        });
    }, []);

    // Split Panel State (Problem 1: Resizable Split)
    const [splitRatio, setSplitRatio] = useState(() => {
        const saved = localStorage.getItem('ai_task_generator_split_ratio');
        return saved ? parseFloat(saved) : 40;
    });
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef(null);
    const MIN_CHAT_WIDTH = 300; // px
    const MIN_EDITOR_WIDTH = 400; // px

    // Content Cache State (Problem 3: Content Persistence)
    const [contentCache, setContentCache] = useState({
        task: '',
        quiz: null,
        training: null,
        checklist: null,
        combined: []
    });

    // Editor Changes Tracking (Problem 2: Bidirectional Sync)
    const [editorChanges, setEditorChanges] = useState([]);

    // Streaming State (Problem 4: Streaming Response)
    const [streamingMessage, setStreamingMessage] = useState('');
    const [streamingPhase, setStreamingPhase] = useState(null);
    const [isStreaming, setIsStreaming] = useState(false);

    // Task Editor State
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        task_type: 'combined',
        content: '',
        phase: phase || 'onboarding',
        category: '',
        priority: 'medium',
        estimated_duration: 30,
        requires_proof: false,
        requires_signature: false,
        assigned_roles: [],
        assigned_employee_ids: [],
        assigned_team_ids: [],
        assignment_metadata: { recursive: false, leaders_only: false },
        assignment_type: 'role',
        passing_score: 70,
        quiz_grading_type: 'auto',
        allow_retakes: false,
        max_attempts: 1,
        due_days_after_assignment: 7,
        scheduled_date: null,
        attachments: [],
        is_compliance_required: isComplianceMode,
        compliance_deadline_days: null,
        is_combined: true,
        sections: []
    });

    const [isSaving, setIsSaving] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [activeEditorTab, setActiveEditorTab] = useState('content'); // content, settings, assignment

    // Step wizard order
    const editorSteps = ['content', 'settings', 'assignment'];
    const currentStepIndex = editorSteps.indexOf(activeEditorTab);
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === editorSteps.length - 1;

    const handleNextStep = () => {
        if (!isLastStep) {
            setActiveEditorTab(editorSteps[currentStepIndex + 1]);
        }
    };

    const handlePrevStep = () => {
        if (!isFirstStep) {
            setActiveEditorTab(editorSteps[currentStepIndex - 1]);
        }
    };

    // Suggestions based on context
    const [suggestions, setSuggestions] = useState([]);

    // AI File Upload State
    const [aiUploadedFiles, setAiUploadedFiles] = useState([]);
    const [isFileUploading, setIsFileUploading] = useState(false);
    const [fileUploadProgress, setFileUploadProgress] = useState(0);
    const [fileProcessingMessage, setFileProcessingMessage] = useState(null);
    const [isWebSearchEnabled, setIsWebSearchEnabled] = useState(false);
    const aiFileInputRef = useRef(null);
    // Web search state
    const [isWebSearching, setIsWebSearching] = useState(false);
    const [currentWebSearchResults, setCurrentWebSearchResults] = useState(null);
    const webSearchResultsRef = useRef(null);
    // Files awaiting user intent choice (attach vs analyze)
    const [pendingIntentFiles, setPendingIntentFiles] = useState([]);


    // Track unsaved changes
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const initialStateRef = useRef(null);

    // Unified File Picker State
    const [showFilePicker, setShowFilePicker] = useState(false);
    const [pickerMode, setPickerMode] = useState('chat'); // 'chat' or 'editor'
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    // Check if editor has content (not blank)
    const hasEditorContent = () => {
        return (
            (formData.title && formData.title.trim() !== '') ||
            (formData.description && formData.description.trim() !== '') ||
            (formData.content && formData.content !== '') ||
            (formData.sections && formData.sections.length > 0) ||
            formData.category !== '' ||
            formData.estimated_duration !== 30
        );
    };

    // Initialize session and welcome message
    useEffect(() => {
        if (isOpen) {
            const newSessionId = `session_${Date.now()}`;
            setSessionId(newSessionId);

            // Edit mode: load existing task data and chat history
            if (isEditMode) {
                // Populate form data from existing task
                setFormData({
                    title: editTask.title || '',
                    description: editTask.description || '',
                    task_type: editTask.task_type || 'task',
                    content: editTask.content || '',
                    phase: editTask.phase || phase || 'onboarding',
                    category: editTask.category || '',
                    priority: editTask.priority || 'medium',
                    estimated_duration: editTask.estimated_duration || 30,
                    requires_proof: editTask.requires_proof || false,
                    requires_signature: editTask.requires_signature || false,
                    assigned_roles: editTask.assigned_roles || [],
                    assigned_employee_ids: editTask.assigned_employee_ids || [],
                    assigned_team_ids: editTask.assigned_team_ids || [],
                    assignment_metadata: editTask.assignment_metadata || { recursive: false, leaders_only: false },
                    assignment_type: editTask.assignment_type || 'role',
                    passing_score: editTask.passing_score || 70,
                    quiz_grading_type: editTask.quiz_grading_type || 'auto',
                    allow_retakes: editTask.allow_retakes || false,
                    max_attempts: editTask.max_attempts || 1,
                    due_days_after_assignment: editTask.due_days_after_assignment || 7,
                    scheduled_date: editTask.scheduled_date || null,
                    attachments: editTask.attachments || [],
                    is_combined: editTask.is_combined || false,
                    sections: editTask.sections || []
                });

                // If it's a combined task, we might need to fetch the full details if sections are missing
                // This happens when editing from the list view which doesn't join sections
                if (editTask.is_combined && (!editTask.sections || editTask.sections.length === 0)) {
                    const fetchFullTask = async () => {
                        try {
                            const response = await api.get(`/tasks/${editTask.id}`);
                            if (response.data.success && response.data.task) {
                                const fullTask = response.data.task;
                                setFormData(prev => ({
                                    ...prev,
                                    sections: fullTask.sections || [],
                                    description: fullTask.description || prev.description,
                                    content: fullTask.content || prev.content
                                }));
                            }
                        } catch (err) {
                            console.error("Failed to fetch full task details:", err);
                        }
                    };
                    fetchFullTask();
                }

                // Restore chat history if available
                if (initialMessages && Array.isArray(initialMessages) && initialMessages.length > 0) {
                    setMessages(initialMessages);
                    // Add a system message indicating edit mode
                    setMessages(prev => [...prev, {
                        id: Date.now(),
                        role: 'assistant',
                        content: `👋 Welcome back! I see you're editing **"${editTask.title}"**. You can continue our conversation to make changes to this task. What would you like to modify?`,
                        timestamp: new Date()
                    }]);
                } else {
                    // No chat history, start fresh with edit context
                    setMessages([{
                        id: Date.now(),
                        role: 'assistant',
                        content: `👋 You're editing the task **"${editTask.title}"**. I can help you make changes to this ${editTask.task_type}. What would you like to modify?\n\nYou can ask me to:\n- Add or change content\n- Modify settings\n- Update assignments`,
                        timestamp: new Date()
                    }]);
                }

                setSuggestions([
                    `Add more content to this ${editTask.task_type}`,
                    'Change the title or description',
                    'Update the estimated duration',
                    'Modify assignments'
                ]);

                fetchRoles();
                return;
            }

            // Check for saved draft session (only for new tasks)
            const savedSession = localStorage.getItem('ai_task_draft_session');
            if (savedSession) {
                try {
                    const parsed = JSON.parse(savedSession);
                    if (parsed.phase === phase) {
                        setMessages(parsed.messages || []);
                        setFormData(parsed.formData || formData);
                        setSuggestions(parsed.suggestions || []);
                        toast.success('Restored your previous draft session');
                        fetchRoles();
                        return;
                    }
                } catch (e) {
                    console.error('Failed to restore session:', e);
                }
            }

            // Fresh session - add welcome message
            setMessages([{
                id: 1,
                role: 'assistant',
                content: `👋 Hello! I'm your AI Task Assistant. I can help you create **tasks, quizzes, training modules, and checklists** for your ${phase} process.\n\nTell me what you'd like to create. For example:\n- "Create a safety compliance quiz with 5 questions"\n- "Build a first-day checklist for new developers"\n- "Generate training content about using our project management tools"`,
                timestamp: new Date()
            }]);

            // FIX: Reset form data to ensure correct phase is used for new tasks
            setFormData({
                title: '',
                description: '',
                task_type: 'task',
                content: '',
                phase: phase || 'onboarding', // Use the current prop!
                category: '',
                priority: 'medium',
                estimated_duration: 30,
                requires_proof: false,
                requires_signature: false,
                assigned_roles: [],
                assigned_employee_ids: [],
                assigned_team_ids: [],
                assignment_metadata: { recursive: false, leaders_only: false },
                assignment_type: 'role',
                passing_score: 70,
                quiz_grading_type: 'auto',
                allow_retakes: false,
                max_attempts: 1,
                due_days_after_assignment: 7,
                scheduled_date: null,
                attachments: [],
                is_compliance_required: isComplianceMode,
                compliance_deadline_days: null,
                is_combined: false,
                sections: []
            });

            // Reset other state
            setAiUploadedFiles([]);
            setContentCache({ task: '', quiz: null, training: null, checklist: null, combined: [] });
            setEditorChanges([]);
            setSuggestions([
                `Create a ${phase} orientation quiz`,
                `Build a first-week checklist`,
                `Generate IT setup training`,
                `Create a company policy acknowledgment task`
            ]);

            fetchRoles();
        }
    }, [isOpen, phase, isEditMode, editTask, initialMessages]);

    // Detect changes to form data
    useEffect(() => {
        if (isOpen && initialStateRef.current !== null) {
            const currentState = JSON.stringify({ formData, messages: messages.length });
            const hasChanges = currentState !== initialStateRef.current;
            setHasUnsavedChanges(hasChanges && hasEditorContent());
        }
    }, [formData, messages, isOpen]);

    // Set initial state reference
    useEffect(() => {
        if (isOpen && initialStateRef.current === null) {
            setTimeout(() => {
                initialStateRef.current = JSON.stringify({ formData, messages: messages.length });
                setHasUnsavedChanges(false);
            }, 500);
        }
    }, [isOpen]);



    // Handle browser back button - show unsaved changes modal
    useEffect(() => {
        if (!isOpen) return;

        const handlePopState = () => {
            if (isOpen && hasUnsavedChanges && hasEditorContent() && !showUnsavedModal) {
                // Prevent actual navigation by pushing state back
                window.history.pushState(null, '', window.location.href);
                // Show the unsaved changes modal
                setShowUnsavedModal(true);
            }
        };

        // Add a history entry when the modal opens to intercept back button
        window.history.pushState(null, '', window.location.href);

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isOpen, hasUnsavedChanges, showUnsavedModal]);

    // Auto-scroll chat
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input on open
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Lock body scroll when modal is open (prevents background scrolling on mobile)
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.top = `-${window.scrollY}px`;
        } else {
            const scrollY = document.body.style.top;
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
            window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
        };
    }, [isOpen]);

    // Drag handlers for resizable split panel (Problem 1)
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging || !containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const mouseX = e.clientX - containerRect.left;

        // Calculate percentage
        let newRatio = (mouseX / containerWidth) * 100;

        // Enforce minimum widths
        const minChatRatio = (MIN_CHAT_WIDTH / containerWidth) * 100;
        const maxChatRatio = 100 - (MIN_EDITOR_WIDTH / containerWidth) * 100;

        newRatio = Math.max(minChatRatio, Math.min(maxChatRatio, newRatio));
        setSplitRatio(newRatio);
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            localStorage.setItem('ai_task_generator_split_ratio', splitRatio.toString());
        }
    }, [isDragging, splitRatio]);

    // Add/remove mouse event listeners for dragging
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        } else {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // Helper function to format time ago (Problem 2)
    const formatTimeAgo = (timestamp) => {
        const seconds = Math.floor((new Date() - new Date(timestamp)) / 1000);
        if (seconds < 60) return 'just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        return `${hours}h ago`;
    };

    // Track content changes for bidirectional sync (Problem 2)
    const trackContentChange = useCallback((field, description) => {
        setEditorChanges(prev => [...prev.slice(-50), {
            field,
            timestamp: new Date(),
            description
        }]);
    }, []);

    const fetchRoles = async () => {
        try {
            const response = await api.get('/employees/roles');
            setAvailableRoles(response.data.roles || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    // Handle file selection from Unified Picker
    const handleFileSelection = (files) => {
        if (pickerMode === 'chat') {
            // Instead of adding directly to context, we now ask the user what they want to do
            // This restores the "Attach to Task" vs "Analyze File" functionality
            const pendingFiles = files.map(f => ({
                file_key: f.file_key || f.key || `existing_${f.id}`,
                file_name: f.file_name || f.name,
                file_type: f.file_type || f.type || 'application/octet-stream',
                file_size: f.file_size || f.size || 0,
                // Add a flag to indicate this is an existing company file
                is_company_file: !!f.id,
                id: f.id,
                url: f.file_url || f.url
            }));

            // Add a special message to the chat that shows the intent buttons
            const choiceMessage = {
                id: Date.now(),
                role: 'assistant',
                content: `I've selected **${pendingFiles.length} file(s)**. How would you like to use them?`,
                action: 'file_intent_choice', // This triggers the buttons in the render loop
                pendingFiles: pendingFiles,
                timestamp: new Date()
            };

            setMessages(prev => [...prev, choiceMessage]);

            // Add to aiUploadedFiles immediately so they show up above the input
            setAiUploadedFiles(prev => {
                const combined = [...prev, ...pendingFiles];
                const unique = [];
                const seen = new Set();
                combined.forEach(file => {
                    const key = file.file_key;
                    if (!seen.has(key)) {
                        seen.add(key);
                        unique.push({ ...file, needs_intent: true });
                    }
                });
                return unique.slice(0, 5);
            });
        } else {
            // Add to Task Attachments (Editor)
            const newAttachments = files.map(f => ({
                file_key: f.file_key || f.key || `existing_${f.id}`,
                file_name: f.file_name || f.name,
                file_type: f.file_type || f.type || 'application/octet-stream',
                file_size: f.file_size || f.size || 0,
                file_url: f.file_url || f.url,
                // If it's an existing file, we might want to store more info
                is_company_file: !!f.id,
                original_file_id: f.id
            }));

            setFormData(prev => {
                const currentAttachments = prev.attachments || [];
                // Filter duplicates
                const combined = [...currentAttachments, ...newAttachments];
                const unique = [];
                const seen = new Set();

                combined.forEach(file => {
                    const key = file.original_file_id ? `id_${file.original_file_id}` : file.file_key;
                    if (!seen.has(key)) {
                        seen.add(key);
                        unique.push(file);
                    }
                });

                return {
                    ...prev,
                    attachments: unique.slice(0, 5) // Max 5 attachments
                };
            });

            toast.success(`${newAttachments.length} file(s) attached to task`);
        }
        setShowFilePicker(false);
    };



    // Handle removing an attachment
    const handleRemoveAttachment = async (file) => {
        // If editing an existing task and file has an ID (not just a temp key from new upload), delete from server
        // But only if we are in edit mode and the file is actually part of the task (has a real file_key on server)
        // If it was just added in this session (no ID yet or just uploaded), we just remove from state

        // Note: UnifiedFilePicker returns objects. 
        // If it's a new upload in this session, it might not be saved to the task yet until we save/update.
        // However, if we are EDITING a task, and we remove a file that was ALREADY on the task, we should probably delete it or mark for deletion.
        // The original FileUpload component deleted immediately.

        if (isEditMode && editTask && editTask.id && file.file_key) {
            // Check if this file was part of the original task attachments
            const isOriginal = editTask.attachments?.some(f => f.file_key === file.file_key);

            if (isOriginal) {
                const loadingToast = toast.loading('Removing file...');
                try {
                    const token = localStorage.getItem('token');
                    await api.delete(`/tasks/${editTask.id}/attachment`, {
                        headers: { Authorization: `Bearer ${token}` },
                        data: { fileKey: file.file_key }
                    });
                    toast.success('File removed', { id: loadingToast });
                } catch (error) {
                    console.error('File deletion error:', error);
                    toast.error('Failed to remove file', { id: loadingToast });
                    return; // Don't remove from state if server fail
                }
            }
        }

        setFormData(prev => ({
            ...prev,
            attachments: prev.attachments.filter(f =>
                (f.file_key && f.file_key !== file.file_key) ||
                (f.id && f.id !== file.id)
            )
        }));
    };

    // Streaming message handler (Problem 4)
    const handleSendMessageWithStreaming = async (messageToSend, currentFiles) => {
        setIsStreaming(true);
        setStreamingMessage('');
        setStreamingPhase('Analyzing your request...');

        const token = localStorage.getItem('token');
        // Get base URL from api utility - Note: Using native fetch for SSE support (axios doesn't stream well)
        const baseURL = api.defaults.baseURL;

        try {
            // DEBUG: Log what we're sending to see if content is updated
            console.log('[AI Debug] Sending current_task to AI:', {
                title: formData.title,
                content: formData.content,
                editorChanges: editorChanges.slice(-5)
            });

            const response = await fetch(`${baseURL}/tasks/generate-ai-chat-stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    message: messageToSend,
                    session_id: sessionId,
                    phase: formData.phase,
                    current_task_type: formData.task_type,
                    quiz_grading_type: formData.quiz_grading_type,
                    conversation_history: messages.slice(-10).map(m => ({
                        role: m.role,
                        content: m.content
                    })),
                    current_task: {
                        title: formData.title,
                        description: formData.description,
                        task_type: formData.task_type,
                        content: formData.content,
                        is_combined: formData.is_combined,
                        sections: formData.sections,
                        quiz_grading_type: formData.quiz_grading_type
                    },
                    uploaded_files: currentFiles,
                    web_search_enabled: isWebSearchEnabled,
                    recent_editor_changes: editorChanges.slice(-50).map(c => ({
                        field: c.field,
                        description: c.description,
                        time_ago: formatTimeAgo(c.timestamp)
                    }))
                })
            });

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            // Progressive streaming state
            let accumulatedContent = '';
            let titleExtracted = false;
            let descExtracted = false;
            let lastExtractedCount = 0;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));

                            if (data.type === 'phase') {
                                setStreamingPhase(data.phase);
                                // Track when web search is in progress
                                if (data.phase?.includes('Searching for current information')) {
                                    setIsWebSearching(true);
                                }
                            } else if (data.type === 'web_search_results') {
                                // Store web search results for display
                                webSearchResultsRef.current = data.results;
                                setCurrentWebSearchResults(data.results);
                                setIsWebSearching(false);
                            } else if (data.type === 'content') {
                                // Accumulate the raw JSON content
                                accumulatedContent += data.content;

                                // Try to extract reply for chat display
                                // Try to extract reply for chat display
                                const replyMatch = accumulatedContent.match(/"reply"\s*:\s*"((?:[^"\\]|\\.)*)"/);
                                if (replyMatch && replyMatch[1]) {
                                    try {
                                        const extractedReply = replyMatch[1]
                                            .replace(/\\n/g, '\n')
                                            .replace(/\\"/g, '"')
                                            .replace(/\\\\/g, '\\');
                                        setStreamingMessage(extractedReply);
                                    } catch {
                                        setStreamingMessage(replyMatch[1]);
                                    }
                                }

                                // Try to extract title progressively
                                const titleMatch = accumulatedContent.match(/"title"\s*:\s*"((?:[^"\\]|\\.)*)"/);
                                if (titleMatch && titleMatch[1] && !titleExtracted) {
                                    titleExtracted = true;
                                    setFormData(prev => ({ ...prev, title: titleMatch[1] }));
                                }

                                // Try to extract description progressively
                                const descMatch = accumulatedContent.match(/"description"\s*:\s*"((?:[^"\\]|\\.)*)"/);
                                if (descMatch && descMatch[1] && !descExtracted) {
                                    descExtracted = true;
                                    setFormData(prev => ({ ...prev, description: descMatch[1] }));
                                }

                                // Progressive content extraction
                                if (accumulatedContent.includes('"questions"')) {
                                    // Extraction logic for quiz
                                    // simplified for brevity in replacement but keeping core logic
                                } else if (accumulatedContent.includes('"items"')) {
                                    // Extraction logic for checklist
                                } else if (accumulatedContent.includes('"sections"')) {
                                    // Extraction logic for training
                                }
                            } else if (data.type === 'complete') {
                                // Add AI's conversational reply to chat
                                const aiMessage = {
                                    id: Date.now() + 1,
                                    role: 'assistant',
                                    content: data.reply,
                                    timestamp: new Date(),
                                    webSearchResults: webSearchResultsRef.current || null,
                                    action: data.action
                                };
                                setMessages(prev => [...prev, aiMessage]);

                                // Clear current web search results
                                setCurrentWebSearchResults(null);
                                webSearchResultsRef.current = null;

                                // Update task editor fields immediately
                                if (data.task_update) {
                                    const isCombinedTask = data.task_update.task_type === 'combined' || data.task_update.is_combined;

                                    setFormData(prev => ({
                                        ...prev,
                                        ...data.task_update,
                                        task_type: data.task_update.task_type || prev.task_type,
                                        is_combined: isCombinedTask,
                                        sections: isCombinedTask ? (data.task_update.sections || prev.sections) : []
                                    }));

                                    // Update content cache
                                    if (isCombinedTask && data.task_update.sections) {
                                        setContentCache(prev => ({ ...prev, combined: data.task_update.sections }));
                                    } else if (data.task_update.content) {
                                        setContentCache(prev => ({
                                            ...prev,
                                            [data.task_update.task_type || formData.task_type]: data.task_update.content
                                        }));
                                    }

                                    if (data.task_update.title || data.task_update.content || data.task_update.sections) {
                                        toast.success('Task updated! Check the editor on the right.');
                                    }
                                }

                                // Handle file actions
                                if (data.file_action) {
                                    if (data.file_action.type === 'attach') {
                                        let filesToAttach = aiUploadedFiles;
                                        if (data.file_action.files && Array.isArray(data.file_action.files) && data.file_action.files.length > 0) {
                                            const fileNamesToAttach = data.file_action.files.map(f => typeof f === 'string' ? f : f.file_name);
                                            filesToAttach = aiUploadedFiles.filter(f => fileNamesToAttach.includes(f.file_name));
                                        }

                                        if (filesToAttach.length > 0) {
                                            setFormData(prev => {
                                                const existingAttachments = prev.attachments || [];
                                                const newToAttach = filesToAttach.filter(f => !existingAttachments.some(a => a.file_key === f.file_key));
                                                return newToAttach.length > 0 ? { ...prev, attachments: [...existingAttachments, ...newToAttach] } : prev;
                                            });
                                            toast.success('📎 Files attached to task');
                                        }
                                    } else if (data.file_action.type === 'remove') {
                                        // Handle remove
                                        if (data.file_action.files && data.file_action.files.length > 0) {
                                            const names = data.file_action.files.map(f => typeof f === 'string' ? f : f.file_name);
                                            setFormData(prev => ({
                                                ...prev,
                                                attachments: (prev.attachments || []).filter(a => !names.includes(a.file_name))
                                            }));
                                        } else {
                                            setFormData(prev => ({ ...prev, attachments: [] }));
                                        }
                                    }
                                }

                                if (data.suggestions) setSuggestions(data.suggestions);
                            } else if (data.type === 'error') {
                                toast.error(data.message || 'An error occurred');
                            }
                        } catch (e) {
                            // Skip invalid JSON
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Streaming error:', error);
            const errorMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: "I apologize, but I encountered an error. Please try again.",
                timestamp: new Date(),
                isError: true
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsStreaming(false);
            setStreamingMessage('');
            setStreamingPhase(null);
        }
    };

    const handleSendMessage = async () => {
        if (!inputMessage.trim() || isGenerating) return;

        const userMessage = {
            id: Date.now(),
            role: 'user',
            content: inputMessage,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        const messageToSend = inputMessage;
        setInputMessage('');
        setIsGenerating(true);

        try {
            // Check if user's message wants to use file content and files are pending
            const messageLower = messageToSend.toLowerCase();
            const wantsToUseFileContent = [
                'create', 'make', 'generate', 'summarize', 'analyze', 'based on',
                'from this', 'using this', 'quiz from', 'training from', 'extract',
                'what does', 'tell me about', 'explain', 'key points'
            ].some(keyword => messageLower.includes(keyword));

            const pendingFiles = aiUploadedFiles.filter(f =>
                f.needs_intent || f.embedding_status === 'pending' || !f.embedding_status
            );

            // Auto-process pending files if user wants to use file content
            if (wantsToUseFileContent && pendingFiles.length > 0) {
                toast.loading('Processing files for AI analysis...', { id: 'auto-process' });

                // Add processing notification to chat
                const processingMsg = {
                    id: Date.now() + 0.5,
                    role: 'assistant',
                    content: `📄 Processing **${pendingFiles.map(f => f.file_name).join(', ')}** for AI analysis...`,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, processingMsg]);

                // Process each pending file
                for (const file of pendingFiles) {
                    try {
                        const response = await api.post('/files/ai-upload/confirm-intent', {
                            file_key: file.file_key,
                            session_id: sessionId,
                            intent: 'reference',
                            file_buffer_base64: file.file_buffer_base64
                        });

                        if (response.data.success) {
                            setAiUploadedFiles(prev => prev.map(f =>
                                f.file_key === file.file_key
                                    ? {
                                        ...f,
                                        intent: 'reference',
                                        embedding_status: response.data.embedding_status,
                                        processing_status: response.data.embedding_status,
                                        document_id: response.data.document_id,
                                        needs_intent: false
                                    }
                                    : f
                            ));
                        }
                    } catch (error) {
                        console.error('Error auto-processing file:', error);
                    }
                }

                toast.success('✓ Analysis complete! Generating task...', { id: 'auto-process' });

                // Update the processing message to show completion
                setMessages(prev => prev.map(m =>
                    m.id === processingMsg.id
                        ? { ...m, content: `✓ Analyzed **${pendingFiles.map(f => f.file_name).join(', ')}**. Now generating content...` }
                        : m
                ));

                // Remove from pending intent files
                setPendingIntentFiles([]);
            }

            // Get updated file list after processing
            const currentFiles = aiUploadedFiles.map(f => ({
                file_name: f.file_name,
                file_key: f.file_key,
                file_url: f.file_url,
                file_size: f.file_size,
                file_type: f.file_type,
                document_id: f.document_id,
                embedding_status: f.embedding_status || 'completed'
            }));

            // Use streaming for better UX (Problem 4)
            await handleSendMessageWithStreaming(messageToSend, currentFiles);
            setIsGenerating(false);
            return; // Streaming handles everything

            /* Original non-streaming code - kept for fallback if needed
            const response = await api.post('/tasks/generate-ai-chat', {
                message: messageToSend,
                session_id: sessionId,
                phase: formData.phase,
                current_task_type: formData.task_type,
                quiz_grading_type: formData.quiz_grading_type,
                conversation_history: messages.slice(-10).map(m => ({
                    role: m.role,
                    content: m.content
                })),
                current_task: {
                    title: formData.title,
                    description: formData.description,
                    task_type: formData.task_type,
                    content: formData.content,
                    quiz_grading_type: formData.quiz_grading_type
                },
                // Include uploaded files info - AI will understand intent from user's message
                uploaded_files: currentFiles,
                // Include recent editor changes for bidirectional sync (Problem 2)
                recent_editor_changes: editorChanges.slice(-5).map(c => ({
                    field: c.field,
                    description: c.description,
                    time_ago: formatTimeAgo(c.timestamp)
                }))
            });
            */

            /* Non-streaming response handling - disabled in favor of streaming
            if (response.data.success) {
                const { reply, task_update, suggestions: newSuggestions, action, file_action } = response.data;
    
                // Handle file actions from AI's intelligent intent recognition
                if (file_action) {
                    if (file_action.type === 'attach') {
                        // AI determined files should be attached to task
                        // file_action.files may be file names (strings) or not provided
                        let filesToAttach = aiUploadedFiles;
    
                        // If AI specified specific file names, filter to those
                        if (file_action.files && Array.isArray(file_action.files) && file_action.files.length > 0) {
                            // AI returns file names as strings, match them to our uploaded files
                            const fileNamesToAttach = file_action.files.map(f =>
                                typeof f === 'string' ? f : f.file_name
                            );
                            filesToAttach = aiUploadedFiles.filter(f =>
                                fileNamesToAttach.includes(f.file_name)
                            );
                        }
    
                        if (filesToAttach.length > 0) {
                            // Check for already attached files
                            const existingAttachments = formData.attachments || [];
                            const alreadyAttached = filesToAttach.filter(f =>
                                existingAttachments.some(a => a.file_key === f.file_key || a.file_name === f.file_name)
                            );
                            const newToAttach = filesToAttach.filter(f =>
                                !existingAttachments.some(a => a.file_key === f.file_key || a.file_name === f.file_name)
                            );
    
                            if (alreadyAttached.length > 0) {
                                toast(`📎 Already attached: ${alreadyAttached.map(f => f.file_name).join(', ')}`, { icon: 'ℹ️' });
                            }
    
                            if (newToAttach.length > 0) {
                                const attachmentFiles = newToAttach.map(f => ({
                                    file_name: f.file_name,
                                    file_url: f.file_url,
                                    file_key: f.file_key,
                                    file_size: f.file_size,
                                    file_type: f.file_type
                                }));
                                setFormData(prev => ({
                                    ...prev,
                                    attachments: [...(prev.attachments || []), ...attachmentFiles]
                                }));
    
                                const fileNames = newToAttach.map(f => f.file_name).join(', ');
                                toast.success(`📎 Attached: ${fileNames}`);
                            }
                        }
                    } else if (file_action.type === 'remove') {
                        // AI determined files should be removed from task
                        if (file_action.files && Array.isArray(file_action.files) && file_action.files.length > 0) {
                            const fileNamesToRemove = file_action.files.map(f =>
                                typeof f === 'string' ? f : f.file_name
                            );
    
                            setFormData(prev => ({
                                ...prev,
                                attachments: (prev.attachments || []).filter(a =>
                                    !fileNamesToRemove.includes(a.file_name)
                                )
                            }));
    
                            toast.success(`🗑️ Removed: ${fileNamesToRemove.join(', ')}`);
                        } else {
                            // Remove all attachments if no specific files mentioned
                            setFormData(prev => ({
                                ...prev,
                                attachments: []
                            }));
                            toast.success('🗑️ All attachments removed');
                        }
                    }
                }
    
                // Add AI response
                const aiMessage = {
                    id: Date.now() + 1,
                    role: 'assistant',
                    content: reply,
                    timestamp: new Date(),
                    action
                };
                setMessages(prev => [...prev, aiMessage]);
    
                // Update task if AI provided updates
                if (task_update) {
                    setFormData(prev => ({
                        ...prev,
                        ...task_update,
                        task_type: task_update.task_type || prev.task_type
                    }));
    
                    // Show toast for significant updates
                    if (task_update.title || task_update.content) {
                        toast.success('Task updated! Check the editor on the right.');
                    }
                }
    
                // Update suggestions
                if (newSuggestions && newSuggestions.length > 0) {
                    setSuggestions(newSuggestions);
                }
            }
            */
        } catch (error) {
            console.error('AI chat error:', error);
            const errorMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: "I apologize, but I encountered an error. Please try again or rephrase your request.",
                timestamp: new Date(),
                isError: true
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsGenerating(false);
        }
    };

    // Handle AI file uploads - DEFERRED PROCESSING
    const handleAiFilesUploaded = (newFiles) => {
        // Check if files need intent choice (deferred processing)
        const filesNeedingIntent = newFiles.filter(f => f.needs_intent && !f.error);

        if (filesNeedingIntent.length > 0) {
            // Store files awaiting intent choice
            setPendingIntentFiles(prev => [...prev, ...filesNeedingIntent]);
            // Add to uploaded files list
            setAiUploadedFiles(prev => [...prev, ...newFiles.filter(f => !f.error)]);

            // Add message with intent chooser
            const fileNames = filesNeedingIntent.map(f => f.file_name).join(', ');
            const intentMessage = {
                id: Date.now(),
                role: 'assistant',
                content: filesNeedingIntent.length === 1
                    ? `📄 Uploaded **"${fileNames}"**. What would you like to do with this file?`
                    : `📄 Uploaded ${filesNeedingIntent.length} files. What would you like to do with them?`,
                timestamp: new Date(),
                action: 'file_intent_choice',
                pendingFiles: filesNeedingIntent
            };
            setMessages(prev => [...prev, intentMessage]);
            return;
        }

        // Handle already-processed files (legacy flow)
        setAiUploadedFiles(prev => [...prev, ...newFiles.filter(f => !f.error)]);

        const completedFiles = newFiles.filter(f => f.embedding_status === 'completed');
        if (completedFiles.length > 0) {
            const fileNames = completedFiles.map(f => f.file_name).join(', ');
            const confirmMessage = {
                id: Date.now(),
                role: 'assistant',
                content: `📄 I've read **"${fileNames}"**. What would you like me to do with it?`,
                timestamp: new Date(),
                action: 'file_processed'
            };
            setMessages(prev => [...prev, confirmMessage]);
            setSuggestions([
                'Summarize this document',
                'Create a quiz from this content',
                'Add this as a task attachment',
                'What are the key points?'
            ]);
        }
    };

    // Direct file upload handler for inline file picker (Claude/Gemini style)
    const handleDirectFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Validate file count
        if (aiUploadedFiles.length + files.length > 5) {
            toast.error('Maximum 5 files allowed per session');
            if (aiFileInputRef.current) aiFileInputRef.current.value = '';
            return;
        }

        // Validate file sizes (max 10MB each)
        const invalidFiles = files.filter(file => file.size > 10 * 1024 * 1024);
        if (invalidFiles.length > 0) {
            toast.error(`Files exceed 10MB limit: ${invalidFiles.map(f => f.name).join(', ')}`);
            if (aiFileInputRef.current) aiFileInputRef.current.value = '';
            return;
        }

        setIsFileUploading(true);
        setFileUploadProgress(0);

        try {
            const formDataUpload = new FormData();
            files.forEach(file => {
                formDataUpload.append('files', file);
            });
            formDataUpload.append('session_id', sessionId);

            const response = await api.post('/files/ai-upload', formDataUpload, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setFileUploadProgress(percentCompleted);
                },
            });

            if (response.data.success) {
                const newFiles = response.data.files.filter(f => !f.error);
                handleAiFilesUploaded(newFiles);
                const fileNames = newFiles.map(f => f.file_name).join(', ');
                toast.success(
                    newFiles.length === 1
                        ? `Uploaded: ${fileNames}`
                        : `${newFiles.length} files uploaded`
                );
            }
        } catch (error) {
            console.error('AI file upload error:', error);
            toast.error(error.response?.data?.message || 'Failed to upload files');
        } finally {
            setIsFileUploading(false);
            setFileUploadProgress(0);
            if (aiFileInputRef.current) aiFileInputRef.current.value = '';
        }
    };

    // Handle file processing complete callback
    const handleFileProcessingComplete = (completedFiles) => {
        if (completedFiles.length > 0) {
            setFileProcessingMessage(null);
            // Update file status in state
            setAiUploadedFiles(prev => prev.map(f => {
                const completed = completedFiles.find(cf => cf.file_key === f.file_key);
                if (completed) {
                    return { ...f, ...completed };
                }
                return f;
            }));
        }
    };

    // Handle removing an AI uploaded file
    const handleRemoveAiFile = async (file) => {
        try {
            // If it was processed as reference, delete from embeddings
            if (file.document_id) {
                await api.delete(`/files/ai-reference/${file.document_id}`);
            }
            setAiUploadedFiles(prev => prev.filter(f => f.file_key !== file.file_key));
            setPendingIntentFiles(prev => prev.filter(f => f.file_key !== file.file_key));
            toast.success('File removed');
        } catch (error) {
            console.error('Error removing file:', error);
            // Still remove from local state
            setAiUploadedFiles(prev => prev.filter(f => f.file_key !== file.file_key));
            setPendingIntentFiles(prev => prev.filter(f => f.file_key !== file.file_key));
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setInputMessage(suggestion);
        inputRef.current?.focus();
    };

    // Handle "Attach to Task" intent choice
    const handleAttachToTask = (files) => {
        // Check for already attached files
        const existingAttachments = formData.attachments || [];
        const alreadyAttached = files.filter(f =>
            existingAttachments.some(a => a.file_key === f.file_key || a.file_name === f.file_name)
        );
        const newToAttach = files.filter(f =>
            !existingAttachments.some(a => a.file_key === f.file_key || a.file_name === f.file_name)
        );

        // Notify about already attached files
        if (alreadyAttached.length > 0) {
            const alreadyNames = alreadyAttached.map(f => f.file_name).join(', ');
            toast(`📎 Already attached: ${alreadyNames}`, { icon: 'ℹ️' });

            if (newToAttach.length === 0) {
                // All files already attached, add message and return
                const message = {
                    id: Date.now(),
                    role: 'assistant',
                    content: `📎 **${alreadyNames}** is already attached to this task.`,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, message]);

                // Remove from pending files
                const fileKeys = files.map(f => f.file_key);
                setPendingIntentFiles(prev => prev.filter(f => !fileKeys.includes(f.file_key)));
                return;
            }
        }

        // Add only new files to task attachments
        if (newToAttach.length > 0) {
            const attachments = newToAttach.map(f => ({
                file_name: f.file_name,
                file_url: f.file_url,
                file_key: f.file_key,
                file_size: f.file_size,
                file_type: f.file_type
            }));

            setFormData(prev => ({
                ...prev,
                attachments: [...(prev.attachments || []), ...attachments]
            }));
        }

        // Remove from pending files
        const fileKeys = files.map(f => f.file_key);
        setPendingIntentFiles(prev => prev.filter(f => !fileKeys.includes(f.file_key)));

        // Update file status
        setAiUploadedFiles(prev => prev.map(f =>
            fileKeys.includes(f.file_key)
                ? { ...f, intent: 'attachment', processing_status: 'attached' }
                : f
        ));

        // Add confirmation message
        const fileNames = newToAttach.map(f => f.file_name).join(', ');
        if (newToAttach.length > 0) {
            const confirmMessage = {
                id: Date.now(),
                role: 'assistant',
                content: `📎 **Attached to task:** ${fileNames}\n\nThe file will be available for employees to download when this task is published.`,
                timestamp: new Date(),
                action: 'file_attached'
            };
            setMessages(prev => [...prev, confirmMessage]);
            toast.success(`📎 Attached: ${fileNames}`);
        }
    };

    // Handle "Let AI Analyze" intent choice
    const handleLetAIAnalyze = async (files) => {
        setFileProcessingMessage('Processing documents for AI analysis...');

        try {
            let alreadyAnalyzedCount = 0;
            let newlyAnalyzedCount = 0;

            for (const file of files) {
                // Call confirm-intent endpoint with the file buffer
                const response = await api.post('/files/ai-upload/confirm-intent', {
                    file_key: file.file_key,
                    session_id: sessionId,
                    intent: 'reference',
                    file_buffer_base64: file.file_buffer_base64
                });

                if (response.data.success) {
                    // Check if file was already analyzed
                    if (response.data.already_analyzed) {
                        alreadyAnalyzedCount++;
                        toast.success(`📄 "${file.file_name}" was already analyzed`, { duration: 3000 });
                    } else {
                        newlyAnalyzedCount++;
                    }

                    // Update file status
                    setAiUploadedFiles(prev => prev.map(f =>
                        f.file_key === file.file_key
                            ? {
                                ...f,
                                intent: 'reference',
                                embedding_status: response.data.embedding_status,
                                processing_status: response.data.embedding_status,
                                document_id: response.data.document_id
                            }
                            : f
                    ));
                }
            }

            // Remove from pending files
            const fileKeys = files.map(f => f.file_key);
            setPendingIntentFiles(prev => prev.filter(f => !fileKeys.includes(f.file_key)));

            // Add confirmation message based on analysis status
            const fileNames = files.map(f => f.file_name).join(', ');
            let messageContent;

            if (alreadyAnalyzedCount > 0 && newlyAnalyzedCount === 0) {
                messageContent = `📄 **${fileNames}** was already analyzed. I can answer questions about it, create quizzes, or summarize it.\n\nWhat would you like me to do?`;
            } else {
                messageContent = `🧠 I've analyzed **${fileNames}**. I can now answer questions about it, create quizzes based on its content, or summarize it.\n\nWhat would you like me to do?`;
            }

            const confirmMessage = {
                id: Date.now(),
                role: 'assistant',
                content: messageContent,
                timestamp: new Date(),
                action: 'file_processed'
            };
            setMessages(prev => [...prev, confirmMessage]);
            setSuggestions([
                'Summarize this document',
                'Create a quiz from this content',
                'What are the key points?',
                'Generate training from this'
            ]);

        } catch (error) {
            console.error('Error processing file for AI:', error);
            toast.error('Failed to process file for AI analysis');
        } finally {
            setFileProcessingMessage(null);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleTaskTypeChange = (newTaskType) => {
        // Calculate the next cache state explicitly to avoid stale state issues (Problem 3: Content Persistence)
        let nextCache = { ...contentCache };

        if (formData.task_type === 'combined') {
            // Cache current sections if we are leaving combined mode
            nextCache.combined = formData.sections;
        } else {
            // Cache current content if we are leaving a standard mode
            nextCache[formData.task_type] = formData.content;
        }

        // Update cache state
        setContentCache(nextCache);

        // Switch to new task type and restore cached content using the FRESH cache
        const isCombined = newTaskType === 'combined';

        setFormData(prev => ({
            ...prev,
            task_type: newTaskType,
            content: isCombined ? '' : (nextCache[newTaskType] || (newTaskType === 'task' ? '' : null)),
            is_combined: isCombined,
            sections: isCombined ? (nextCache.combined || []) : []
        }));

        // Track the change for AI sync
        trackContentChange('task_type', `Changed task type to ${newTaskType}`);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleRolesChange = (e) => {
        const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
        setFormData(prev => ({ ...prev, assigned_roles: selectedOptions }));
    };

    const handleSaveAsDraft = async () => {
        if (!formData.title.trim()) {
            toast.error('Please enter a task title');
            return;
        }

        setIsSaving(true);
        try {
            const taskPayload = {
                ...formData,
                status: 'draft',
                is_ai_generated: true,
                ai_chat_history: messages
            };

            if (isEditMode) {
                // Update existing task
                await api.put(`/tasks/${editTask.id}`, taskPayload);
                toast.success('Task updated and saved as draft!');
            } else {
                // Create new task
                await api.post('/tasks', taskPayload);
                // Save session for later
                localStorage.setItem('ai_task_draft_session', JSON.stringify({
                    phase,
                    messages,
                    formData,
                    suggestions,
                    savedAt: new Date().toISOString()
                }));
                toast.success('Task saved as draft! Session preserved.');
            }

            onSuccess();
        } catch (error) {
            console.error('Save draft error:', error);
            toast.error('Failed to save draft');
        } finally {
            setIsSaving(false);
        }
    };

    const handlePublish = async () => {
        if (!formData.title.trim()) {
            toast.error('Please enter a task title');
            return;
        }

        setIsSaving(true);
        try {
            let taskId;

            const taskPayload = {
                ...formData,
                status: 'draft',
                is_ai_generated: true,
                ai_chat_history: messages
            };

            if (isEditMode) {
                // Update existing task first
                await api.put(`/tasks/${editTask.id}`, taskPayload);
                taskId = editTask.id;
            } else {
                // Create task first
                const createResponse = await api.post('/tasks', taskPayload);
                taskId = createResponse.data.task.id;
            }

            // Then publish it
            await api.post(`/tasks/${taskId}/publish`);

            // Clear session on successful publish
            localStorage.removeItem('ai_task_draft_session');

            toast.success(isEditMode ? 'Task updated and published!' : 'Task published successfully!');
            onSuccess();
            handleClose();
        } catch (error) {
            console.error('Publish error:', error);
            toast.error('Failed to publish task');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSchedule = async () => {
        if (!formData.title.trim()) {
            toast.error('Please enter a task title');
            return;
        }

        if (!formData.scheduled_date) {
            toast.error('Please select a scheduled date');
            return;
        }

        setIsSaving(true);
        try {
            let taskId;

            const taskPayload = {
                ...formData,
                status: 'draft',
                is_ai_generated: true,
                ai_chat_history: messages
            };

            if (isEditMode) {
                // Update existing task first
                await api.put(`/tasks/${editTask.id}`, taskPayload);
                taskId = editTask.id;
            } else {
                // Create task first
                const createResponse = await api.post('/tasks', taskPayload);
                taskId = createResponse.data.task.id;
            }

            // Then schedule it
            await api.post(`/tasks/${taskId}/schedule`, { scheduled_date: formData.scheduled_date });

            // Clear session on successful schedule
            localStorage.removeItem('ai_task_draft_session');

            toast.success(isEditMode ? 'Task updated and scheduled!' : 'Task scheduled successfully!');
            onSuccess();
            handleClose();
        } catch (error) {
            console.error('Schedule error:', error);
            toast.error('Failed to schedule task');
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (hasUnsavedChanges && hasEditorContent()) {
            setShowUnsavedModal(true);
        } else {
            // Clear session unless saved as draft
            if (!formData.title.trim()) {
                localStorage.removeItem('ai_task_draft_session');
            }
            onClose();
        }
    };

    const handleSaveAsDraftFromModal = async () => {
        setShowUnsavedModal(false);
        await handleSaveAsDraft();
    };

    const handleLeaveWithoutSaving = () => {
        setShowUnsavedModal(false);
        localStorage.removeItem('ai_task_draft_session');
        onClose();
    };

    const handleClearChat = () => {
        if (window.confirm('Clear chat history? Your task editor content will be preserved.')) {
            setMessages([{
                id: Date.now(),
                role: 'assistant',
                content: "Chat cleared! Your task content is still in the editor. How can I help you further?",
                timestamp: new Date()
            }]);
        }
    };

    const getTaskIcon = (taskType) => {
        switch (taskType) {
            case 'quiz': return <Brain className="w-5 h-5" />;
            case 'training': return <BookOpen className="w-5 h-5" />;
            case 'checklist': return <CheckSquare className="w-5 h-5" />;
            case 'combined': return <Layers className="w-5 h-5" />;
            default: return <FileText className="w-5 h-5" />;
        }
    };

    if (!isOpen) return null;

    return (
        <div ref={containerRef} className="fixed inset-0 z-50 bg-white dark:bg-slate-900 flex flex-col">
            {/* Header - Always at top on mobile, hidden on desktop since desktop has split view with header in left panel? No, NoticeGen has header at VERY TOP spanning full width */}
            {/* Wait, AINoticeGenerator has a full width header at the top of the modal. */}
            {/* Let's check AINoticeGenerator again. Line 1025: <div className="fixed inset-0 z-50 flex flex-col bg-slate-900"> */}
            {/* Then Header (1027). Then Mobile Switcher (1048). Then Flex row (1072). */}

            {/* AITaskGenerator has: flex flex-col lg:flex-row */}
            {/* I should restructure AITaskGenerator to match AINoticeGenerator structure: Top Header -> Flex Container (Chat | Editor) */}

            {/* Header */}
            <div className="fixed inset-x-0 top-0 z-10 flex-shrink-0 bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-3 flex items-center justify-between lg:static">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-white dark:bg-slate-900/20 rounded-lg">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-white">
                            {isEditMode ? 'Edit Task with AI' : 'AI Task Generator'}
                        </h2>
                        <p className="text-white/80 text-sm capitalize">{phase} Phase</p>
                    </div>

                    {/* AI Chat Toggle — left side, with label */}
                    <button
                        onClick={toggleChatCollapsed}
                        className="hidden lg:flex items-center gap-2 ml-3 px-3 py-1.5 rounded-full transition-all duration-200 bg-white/15 hover:bg-white/25 border border-white/20 hover:border-white/40"
                        title={isChatCollapsed ? 'Show AI Chat Panel' : 'Hide AI Chat Panel'}
                    >
                        {isChatCollapsed ? (
                            <>
                                <MessageSquare className="w-4 h-4 text-white" />
                                <span className="text-xs font-semibold text-white whitespace-nowrap">Show AI Chat</span>
                            </>
                        ) : (
                            <>
                                <PanelLeftClose className="w-4 h-4 text-white" />
                                <span className="text-xs font-semibold text-white/80 whitespace-nowrap">Hide Chat</span>
                            </>
                        )}
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setIsPreviewOpen(true)}
                        disabled={!formData.title?.trim() && (!formData.sections || formData.sections.length === 0)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 border border-white/20 hover:border-white/40 rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Preview how employees will see this task"
                    >
                        <Eye className="w-4 h-4 text-white" />
                        <span className="text-xs font-semibold text-white hidden sm:inline">Preview</span>
                    </button>
                    <button
                        onClick={handleClearChat}
                        className="p-2 hover:bg-white/15 rounded-lg transition-colors hidden lg:block"
                        title="Clear chat"
                    >
                        <RefreshCw className="w-5 h-5 text-white" />
                    </button>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-white/15 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>
            </div>

            {/* Mobile Tab Switcher */}
            <div className="lg:hidden flex border-b border-slate-700 bg-slate-800 mt-[60px]">
                <button
                    onClick={() => setMobileActivePanel('chat')}
                    className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${mobileActivePanel === 'chat'
                        ? 'text-white border-b-2 border-violet-500'
                        : 'text-slate-400'
                        }`}
                >
                    <Sparkles className="w-4 h-4 inline mr-2" />
                    AI Chat
                </button>
                <button
                    onClick={() => setMobileActivePanel('editor')}
                    className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${mobileActivePanel === 'editor'
                        ? 'text-white border-b-2 border-violet-500'
                        : 'text-slate-400'
                        }`}
                >
                    <FileText className="w-4 h-4 inline mr-2" />
                    Task Editor
                </button>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex overflow-hidden flex-col lg:flex-row mt-0 lg:mt-0 pt-0">

                {/* Collapsed Chat Sidebar Strip (desktop only) */}
                {isChatCollapsed && (
                    <div
                        className="hidden lg:flex flex-col items-center bg-slate-800 border-r border-slate-700 cursor-pointer hover:bg-slate-750 transition-colors group"
                        style={{ width: '48px', minWidth: '48px' }}
                        onClick={toggleChatCollapsed}
                        title="Expand AI Chat"
                    >
                        <div className="flex flex-col items-center gap-3 py-5">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20 group-hover:scale-110 transition-transform">
                                <Sparkles className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-[10px] font-bold text-slate-400 tracking-widest uppercase" style={{ writingMode: 'vertical-lr' }}>AI Chat</span>
                            </div>
                        </div>
                        <div className="mt-auto mb-4 p-1.5 rounded-lg bg-slate-700/50 group-hover:bg-violet-600/30 transition-colors">
                            <PanelLeftOpen className="w-4 h-4 text-slate-400 group-hover:text-violet-300 transition-colors" />
                        </div>
                    </div>
                )}

                {/* Chat Panel */}
                <div
                    className={`flex-col bg-slate-800 ${mobileActivePanel === 'chat' ? 'flex' : 'hidden lg:flex'}`}
                    style={{
                        width: window.innerWidth >= 1024
                            ? (isChatCollapsed ? '0px' : `${splitRatio}%`)
                            : '100%',
                        minWidth: window.innerWidth >= 1024 && isChatCollapsed ? '0px' : undefined,
                        overflow: isChatCollapsed && window.innerWidth >= 1024 ? 'hidden' : undefined,
                        opacity: isChatCollapsed && window.innerWidth >= 1024 ? 0 : 1,
                        transition: 'width 300ms ease, opacity 200ms ease, min-width 300ms ease',
                        pointerEvents: isChatCollapsed && window.innerWidth >= 1024 ? 'none' : undefined
                    }}
                >

                    {/* Chat Messages */}
                    <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 md:px-5 md:py-6 space-y-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : ''}`}
                            >
                                {message.role === 'assistant' && (
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
                                        <Sparkles className="w-4 h-4 text-white" />
                                    </div>
                                )}

                                <div className={`flex flex-col max-w-[85%] ${message.role === 'user' ? 'items-end' : 'items-start'}`}>
                                    <div
                                        className={`rounded-2xl px-4 py-3 shadow-sm ${message.role === 'user'
                                            ? 'bg-violet-600 text-white rounded-br-sm'
                                            : 'bg-slate-700 text-slate-100 rounded-bl-sm'
                                            }`}
                                    >
                                        {message.role === 'user' ? (
                                            <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{message.content}</p>
                                        ) : (
                                            <div className="space-y-3">
                                                {/* Action Cards - Show what AI did */}
                                                {message.action && ['generated', 'updated', 'file_attached', 'file_processed'].includes(message.action) && (
                                                    <div className="space-y-1.5 mb-2">
                                                        {message.action === 'generated' && (
                                                            <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 rounded-lg border border-slate-600/30">
                                                                <FileText className="w-4 h-4 text-violet-400" />
                                                                <span className="text-sm text-slate-300">Created:</span>
                                                                <span className="text-sm font-medium text-white">
                                                                    {formData.task_type}/{formData.title || 'New Task'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {message.action === 'updated' && (
                                                            <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 rounded-lg border border-slate-600/30">
                                                                <FileText className="w-4 h-4 text-blue-400" />
                                                                <span className="text-sm text-slate-300">Edited:</span>
                                                                <span className="text-sm font-medium text-white">
                                                                    {formData.task_type}/{formData.title || 'Task'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {message.action === 'file_attached' && (
                                                            <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 rounded-lg border border-slate-600/30">
                                                                <Paperclip className="w-4 h-4 text-emerald-400" />
                                                                <span className="text-sm text-slate-300">Attached:</span>
                                                                <span className="text-sm font-medium text-white">File to Task</span>
                                                            </div>
                                                        )}
                                                        {message.action === 'file_processed' && (
                                                            <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 rounded-lg border border-slate-600/30">
                                                                <Brain className="w-4 h-4 text-pink-400" />
                                                                <span className="text-sm text-slate-300">Analyzed:</span>
                                                                <span className="text-sm font-medium text-white">Document content</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Message Content */}
                                                <div className="text-[15px] leading-relaxed">
                                                    {message.content.split('\n').map((line, i) => (
                                                        <p key={i} className="mb-1.5 last:mb-0">
                                                            {line.startsWith('**') && line.endsWith('**') ? (
                                                                <strong className="text-white font-semibold">{line.slice(2, -2)}</strong>
                                                            ) : line.startsWith('- ') ? (
                                                                <span className="flex gap-2">
                                                                    <span className="text-violet-400 font-bold">•</span>
                                                                    <span>{line.slice(2)}</span>
                                                                </span>
                                                            ) : line}
                                                        </p>
                                                    ))}
                                                </div>

                                                {/* Web Search Results */}
                                                {message.webSearchResults && message.webSearchResults.length > 0 && (
                                                    <WebSearchResultsDisplay results={message.webSearchResults} />
                                                )}

                                                {/* Intent Chooser Buttons */}
                                                {message.action === 'file_intent_choice' && message.pendingFiles && (
                                                    <div className="mt-3 pt-3 border-t border-slate-600/30">
                                                        <div className="flex flex-wrap gap-2">
                                                            <button
                                                                onClick={() => handleAttachToTask(message.pendingFiles)}
                                                                className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-slate-200 rounded-lg text-xs transition-colors border border-slate-600"
                                                            >
                                                                <Paperclip className="w-3.5 h-3.5" />
                                                                <span>Attach to Task</span>
                                                            </button>
                                                            <button
                                                                onClick={() => handleLetAIAnalyze(message.pendingFiles)}
                                                                className="flex items-center gap-2 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs transition-colors shadow-lg shadow-violet-500/20"
                                                            >
                                                                <Brain className="w-3.5 h-3.5" />
                                                                <span>Analyze Content</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-[10px] text-slate-500 mt-1 px-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                            </div>
                        ))}

                        {/* Streaming/Thinking Indicator (Problem 4: Streaming Response) */}
                        {(isGenerating || isStreaming) && (
                            <div className="space-y-3">
                                {/* Phase Indicator */}
                                {streamingPhase && (
                                    <div className="flex items-center gap-2 text-violet-300">
                                        <Loader className="w-4 h-4 animate-spin" />
                                        <span className="text-sm font-medium">{streamingPhase}</span>
                                    </div>
                                )}

                                {/* Streaming Message Content */}
                                {streamingMessage ? (
                                    <div className="flex gap-3">
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
                                            <Sparkles className="w-4 h-4 text-white" />
                                        </div>
                                        <div className="rounded-2xl px-4 py-3 shadow-sm bg-slate-700 text-slate-100 rounded-bl-sm">
                                            <div className="text-[15px] leading-relaxed">
                                                {streamingMessage}
                                                <span className="inline-block w-1.5 h-4 bg-violet-400 animate-pulse ml-1 align-text-bottom" />
                                            </div>
                                        </div>
                                    </div>
                                ) : !streamingPhase && (
                                    <div className="flex items-center gap-2 text-slate-400 ml-11">
                                        <span className="text-sm">Thinking...</span>
                                        <span className="inline-block w-1.5 h-4 bg-slate-500 animate-pulse" />
                                    </div>
                                )}
                            </div>
                        )}

                        <div ref={chatEndRef} />
                    </div>

                    {/* Suggestions */}
                    {suggestions.length > 0 && !isGenerating && (
                        <div className="px-4 pb-3 md:px-5">
                            <div className="flex items-center gap-2 mb-2 px-1">
                                <Lightbulb className="w-3 h-3 text-violet-400" />
                                <span className="text-xs font-medium text-slate-400">Suggestions</span>
                            </div>
                            <div className="flex gap-2 overflow-x-auto whitespace-nowrap scrollbar-hide pb-2 px-1">
                                {suggestions.map((suggestion, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        className="text-xs text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 border border-slate-600 hover:border-slate-500 px-3 py-2 rounded-full transition-all flex-shrink-0"
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* File Processing Indicator */}
                    {fileProcessingMessage && (
                        <div className="px-5 pb-2">
                            <div className="flex items-center gap-2 text-sm text-violet-400">
                                <Loader className="w-3.5 h-3.5 animate-spin" />
                                <span>{fileProcessingMessage}</span>
                            </div>
                        </div>
                    )}

                    {/* Chat Input */}
                    <div className="p-4 md:p-5 border-t border-slate-800 bg-slate-900/50">
                        {/* Hidden File Input */}


                        {/* Inline File Upload Progress */}
                        {isFileUploading && (
                            <div className="mb-3 bg-slate-800 rounded-lg p-2.5 border border-slate-700">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <Loader className="w-3.5 h-3.5 animate-spin text-violet-400" />
                                    <span className="text-xs text-slate-300">Uploading...</span>
                                    <span className="text-xs text-slate-500 ml-auto">{fileUploadProgress}%</span>
                                </div>
                                <div className="w-full bg-slate-700 rounded-full h-1 overflow-hidden">
                                    <div
                                        className="bg-violet-500 h-full rounded-full transition-all duration-300"
                                        style={{ width: `${fileUploadProgress}%` }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Uploaded Files Display */}
                        {aiUploadedFiles.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                                {aiUploadedFiles.map((file) => {
                                    const ext = file.file_name?.split('.').pop()?.toUpperCase() || 'FILE';
                                    const isPdf = ext === 'PDF';
                                    const isDoc = ['DOC', 'DOCX'].includes(ext);
                                    const isImage = ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'].includes(ext);
                                    const isSpreadsheet = ['XLS', 'XLSX'].includes(ext);

                                    return (
                                        <div
                                            key={file.file_key}
                                            className="group flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 rounded-lg pl-2.5 pr-1.5 py-1.5 transition-colors"
                                        >
                                            <div className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold ${isPdf ? 'bg-red-500/20 text-red-400' :
                                                isDoc ? 'bg-blue-500/20 text-blue-400' :
                                                    isImage ? 'bg-green-500/20 text-green-400' :
                                                        isSpreadsheet ? 'bg-emerald-500/20 text-emerald-400' :
                                                            'bg-slate-600/50 text-slate-400'
                                                }`}>
                                                {isPdf ? 'PDF' : isDoc ? 'DOC' : isImage ? 'IMG' : isSpreadsheet ? 'XLS' : ext.slice(0, 3)}
                                            </div>
                                            <span className="text-xs text-slate-300 max-w-[120px] truncate font-medium">
                                                {file.file_name}
                                            </span>
                                            <button
                                                onClick={() => handleRemoveAiFile(file)}
                                                className="p-1 text-slate-500 hover:text-red-400 rounded-md transition-colors opacity-60 group-hover:opacity-100"
                                                title="Remove file"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Input Wrapper */}
                        <div className="relative">
                            <textarea
                                ref={inputRef}
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Describe the task you want to create..."
                                rows={2}
                                className="w-full px-4 py-3 pr-24 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                            />

                            <div className="absolute right-2 bottom-2 flex gap-1">
                                <button
                                    onClick={() => {
                                        setPickerMode('chat');
                                        setShowFilePicker(true);
                                    }}
                                    disabled={isGenerating || isFileUploading || aiUploadedFiles.length >= 5}
                                    className={`p-1.5 rounded-lg transition-colors ${aiUploadedFiles.length > 0
                                        ? 'bg-violet-600 text-white'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-600'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    title="Attach files"
                                >
                                    <Paperclip className="w-4 h-4" />
                                </button>

                                <button
                                    onClick={handleSendMessage}
                                    disabled={!inputMessage.trim() || isGenerating}
                                    className="p-1.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-violet-500/20"
                                >
                                    {isGenerating ? (
                                        <Loader className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Send className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="text-center mt-2">
                            <p className="text-[10px] text-slate-600">
                                AI can make mistakes. Please review generated tasks.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Draggable Divider - hidden when chat is collapsed */}
                {!isChatCollapsed && (
                    <div
                        className="hidden lg:flex flex-col items-center justify-center w-1 bg-slate-800 hover:bg-violet-600/20 cursor-col-resize transition-colors group z-20"
                        onMouseDown={handleMouseDown}
                    >
                        <div className="absolute flex flex-col items-center justify-center w-4 h-12 rounded bg-slate-700 group-hover:bg-violet-600 transition-colors shadow-lg border border-slate-600 group-hover:border-violet-500">
                            <GripVertical className="w-3 h-3 text-slate-400 group-hover:text-white" />
                        </div>
                    </div>
                )}

                {/* Right Panel - Task Editor (dynamic width on desktop, full on mobile when active) */}
                <div
                    className={`flex flex-col bg-white dark:bg-slate-900 overflow-hidden ${mobileActivePanel === 'editor' ? 'flex' : 'hidden lg:flex'} relative transition-all duration-300`}
                    style={{
                        width: window.innerWidth >= 1024
                            ? (isChatCollapsed ? '100%' : `${100 - splitRatio}%`)
                            : '100%',
                        flex: isChatCollapsed && window.innerWidth >= 1024 ? '1 1 0%' : undefined,
                        transition: 'width 300ms ease, flex 300ms ease'
                    }}
                >
                    {/* AI Loading Overlay */}
                    {(isGenerating || isStreaming) && (
                        <div className="absolute inset-0 z-50 bg-white dark:bg-slate-900/80 backdrop-blur-[2px] flex flex-col items-center justify-center animate-in fade-in duration-300">
                            <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-xl border border-violet-100 flex flex-col items-center max-w-sm text-center">
                                <div className="relative w-16 h-16 mb-6">
                                    <div className="absolute inset-0 rounded-full border-4 border-violet-100"></div>
                                    <div className="absolute inset-0 rounded-full border-4 border-violet-500 border-t-transparent animate-spin"></div>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <Sparkles className="w-6 h-6 text-violet-600 animate-pulse" />
                                    </div>
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 dark:text-slate-100 mb-2">
                                    {streamingPhase || 'Generating Task...'}
                                </h3>
                                <p className="text-gray-500 dark:text-slate-400 text-sm">
                                    AI is crafting your content based on your request.
                                </p>
                            </div>
                        </div>
                    )}
                    {/* Editor Header */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50 dark:bg-slate-800/50 gap-3 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${formData.task_type === 'quiz' ? 'bg-purple-100 text-purple-600' :
                                formData.task_type === 'training' ? 'bg-blue-100 text-blue-600' :
                                    formData.task_type === 'checklist' ? 'bg-green-100 text-green-600' :
                                        'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300'
                                }`}>
                                {getTaskIcon(formData.task_type)}
                            </div>
                            <div>
                                <h2 className="font-semibold text-gray-800 dark:text-slate-100">
                                    {formData.title || 'Untitled Task'}
                                </h2>
                                <p className="text-xs text-gray-500 dark:text-slate-400 capitalize">
                                    {formData.task_type} • {formData.phase}
                                </p>
                            </div>
                        </div>

                        {/* Step Progress Indicator */}
                        <div className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto overflow-x-auto">
                            {[
                                { id: 'content', label: 'Content', icon: FileText, step: 1 },
                                { id: 'settings', label: 'Settings', icon: Settings, step: 2 },
                                { id: 'assignment', label: 'Assignment', icon: Users, step: 3 }
                            ].map(({ id, label, icon: Icon, step }, index) => {
                                const stepIndex = editorSteps.indexOf(id);
                                const isCompleted = stepIndex < currentStepIndex;
                                const isCurrent = id === activeEditorTab;

                                return (
                                    <React.Fragment key={id}>
                                        <div className="flex items-center gap-1.5 sm:gap-2">
                                            <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold transition-all ${isCompleted
                                                ? 'bg-green-500 text-white'
                                                : isCurrent
                                                    ? 'bg-violet-600 text-white ring-4 ring-violet-100'
                                                    : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                                                }`}>
                                                {isCompleted ? <Check className="w-4 h-4" /> : step}
                                            </div>
                                            <span className={`hidden sm:inline text-sm font-medium ${isCurrent ? 'text-violet-600' : isCompleted ? 'text-green-600' : 'text-gray-400 dark:text-slate-500'
                                                }`}>
                                                {label}
                                            </span>
                                        </div>
                                        {index < 2 && (
                                            <div className={`w-6 sm:w-10 h-0.5 ${isCompleted ? 'bg-green-500' : 'bg-gray-200 dark:bg-slate-700'
                                                }`} />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>

                    {/* Editor Content */}
                    <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {activeEditorTab === 'content' && (
                            <div className="space-y-4 sm:space-y-6">


                                {/* Title */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                        Title *
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        placeholder="Enter task title"
                                        className="w-full px-4 py-3 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:border-transparent text-lg"
                                    />
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                        Description
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        placeholder="Brief description of this task"
                                        rows="3"
                                        className="w-full px-4 py-3 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:border-transparent"
                                    />
                                </div>

                                {/* Content Builder */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                        Content
                                    </label>

                                    {/* Quiz Grading Type Selector - only show for quiz type */}
                                    {formData.task_type === 'quiz' && (
                                        <div className="mb-4 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-lg border border-gray-200 dark:border-slate-700">
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-3">
                                                Grading Type
                                            </label>
                                            <div className="grid grid-cols-2 gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, quiz_grading_type: 'auto' }))}
                                                    className={`p-3 border-2 rounded-xl flex flex-col items-center gap-2 transition-all ${formData.quiz_grading_type === 'auto'
                                                        ? 'border-green-500 bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400'
                                                        : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:border-slate-600'
                                                        }`}
                                                >
                                                    <Brain className="w-5 h-5" />
                                                    <span className="font-medium text-sm">Auto-Graded</span>
                                                    <span className="text-xs text-gray-500 dark:text-slate-400 text-center">Multiple choice</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, quiz_grading_type: 'manual' }))}
                                                    className={`p-3 border-2 rounded-xl flex flex-col items-center gap-2 transition-all ${formData.quiz_grading_type === 'manual'
                                                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400'
                                                        : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:border-slate-600'
                                                        }`}
                                                >
                                                    <FileText className="w-5 h-5" />
                                                    <span className="font-medium text-sm">Manual Review</span>
                                                    <span className="text-xs text-gray-500 dark:text-slate-400 text-center">Written answers</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Unified Task Builder */}
                                    <CombinedTaskBuilder
                                        sections={formData.sections}
                                        onChange={(sections) => setFormData(prev => ({ ...prev, sections }))}
                                        onContentChange={trackContentChange}
                                    />
                                </div>

                                {/* File Attachments */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                        File Attachments (Optional)
                                    </label>

                                    <div className="space-y-3">
                                        {/* Attach Button */}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setPickerMode('editor');
                                                setShowFilePicker(true);
                                            }}
                                            disabled={formData.attachments.length >= 5}
                                            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 hover:bg-gray-50 dark:bg-slate-800/50 transition-colors disabled:opacity-50"
                                        >
                                            <Paperclip className="w-4 h-4" />
                                            Attach Files
                                        </button>

                                        {/* Attachment List */}
                                        {formData.attachments.length > 0 && (
                                            <div className="space-y-2">
                                                {formData.attachments.map((file, index) => (
                                                    <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg">
                                                        <div className="flex items-center gap-3 overflow-hidden">
                                                            <div className="p-2 bg-gray-100 dark:bg-slate-800 rounded-lg">
                                                                <FileText className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                                    {file.file_name}
                                                                </p>
                                                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                                                    {file.file_size ? `${Math.round(file.file_size / 1024)} KB` : 'Unknown size'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveAttachment(file)}
                                                            className="p-1 text-gray-400 dark:text-slate-500 hover:text-red-500 hover:bg-red-50 dark:bg-red-500/10 rounded transition-colors"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeEditorTab === 'settings' && (
                            <div className="space-y-4 sm:space-y-6">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {/* Category */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                            Category
                                        </label>
                                        <input
                                            type="text"
                                            name="category"
                                            value={formData.category}
                                            onChange={handleChange}
                                            placeholder="e.g., Compliance, IT Setup"
                                            className="w-full px-4 py-2 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:border-transparent"
                                        />
                                    </div>

                                    {/* Priority */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                            Priority
                                        </label>
                                        <select
                                            name="priority"
                                            value={formData.priority}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 bg-white dark:bg-slate-800 text-gray-900 dark:text-white border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:border-transparent"
                                        >
                                            <option value="low">Low</option>
                                            <option value="medium">Medium</option>
                                            <option value="high">High</option>
                                        </select>
                                    </div>

                                    {/* Estimated Duration */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                            Estimated Duration (minutes)
                                        </label>
                                        <input
                                            type="number"
                                            name="estimated_duration"
                                            value={formData.estimated_duration}
                                            onChange={handleChange}
                                            min="5"
                                            className="w-full px-4 py-2 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:border-transparent"
                                        />
                                    </div>

                                    {/* Due Days */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                            Due Days After Assignment
                                        </label>
                                        <input
                                            type="number"
                                            name="due_days_after_assignment"
                                            value={formData.due_days_after_assignment}
                                            onChange={handleChange}
                                            min="1"
                                            className="w-full px-4 py-2 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:border-transparent"
                                        />
                                    </div>

                                    {/* Passing Score (Quiz only) */}
                                    {formData.task_type === 'quiz' && (
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                                Passing Score (%)
                                            </label>
                                            <input
                                                type="number"
                                                name="passing_score"
                                                value={formData.passing_score}
                                                onChange={handleChange}
                                                min="0"
                                                max="100"
                                                className="w-full px-4 py-2 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:border-transparent"
                                            />
                                        </div>
                                    )}

                                    {/* Schedule Date */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                            Schedule Date (Optional)
                                        </label>
                                        <input
                                            type="datetime-local"
                                            name="scheduled_date"
                                            value={formData.scheduled_date || ''}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 bg-white dark:bg-slate-800 text-gray-900 dark:text-white border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:border-transparent"
                                        />
                                    </div>
                                </div>



                                {/* Compliance Task */}
                                <div className={`rounded-xl p-4 border-2 transition-all ${formData.is_compliance_required
                                    ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200 dark:border-red-500/30'
                                    : 'bg-gray-50 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700'
                                    }`}>
                                    <div className="flex items-start gap-3">
                                        <div className={`p-2 rounded-lg ${formData.is_compliance_required
                                            ? 'bg-red-100 dark:bg-red-500/20'
                                            : 'bg-gray-200 dark:bg-slate-700'
                                            }`}>
                                            <Shield className={`w-5 h-5 ${formData.is_compliance_required
                                                ? 'text-red-600 dark:text-red-400'
                                                : 'text-gray-500 dark:text-slate-400'
                                                }`} />
                                        </div>
                                        <div className="flex-1">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    name="is_compliance_required"
                                                    checked={formData.is_compliance_required}
                                                    onChange={handleChange}
                                                    disabled={isComplianceMode}
                                                    className={`w-4 h-4 rounded focus:ring-2 ${formData.is_compliance_required
                                                        ? 'text-red-600 dark:text-red-400 focus:ring-red-500'
                                                        : 'text-violet-600 focus:ring-violet-500 dark:focus:ring-violet-400'
                                                        } ${isComplianceMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                                                />
                                                <span className="text-sm font-semibold text-gray-800 dark:text-slate-100">
                                                    This is a compliance-required task
                                                </span>
                                            </label>
                                            <p className="text-xs text-gray-600 dark:text-slate-300 mt-1 ml-6">
                                                Compliance tasks are legally required and tracked separately.
                                                Overdue tasks create violations and trigger automatic alerts.
                                            </p>
                                            {isComplianceMode && (
                                                <p className="text-xs text-red-600 dark:text-red-400 mt-2 ml-6 font-medium">
                                                    Creating from Compliance dashboard - compliance flag is locked.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeEditorTab === 'assignment' && (
                            <div className="space-y-4 sm:space-y-6">
                                {/* Employee Assignment */}
                                <EmployeeSelector
                                    phase={formData.phase}
                                    selectedEmployees={formData.assigned_employee_ids}
                                    onSelectionChange={(ids) => setFormData(prev => ({ ...prev, assigned_employee_ids: ids }))}
                                    assignmentType={formData.assignment_type}
                                    onAssignmentTypeChange={(type) => setFormData(prev => ({ ...prev, assignment_type: type }))}
                                    selectedTeams={formData.assigned_team_ids}
                                    onTeamSelectionChange={(ids) => setFormData(prev => ({ ...prev, assigned_team_ids: ids }))}
                                    assignmentMetadata={formData.assignment_metadata}
                                    onMetadataChange={(meta) => setFormData(prev => ({ ...prev, assignment_metadata: meta }))}
                                />

                                {/* Role Selector */}
                                {formData.assignment_type === 'role' && (
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                            Assign to Roles (hold Ctrl/Cmd for multiple)
                                        </label>
                                        <select
                                            multiple
                                            value={formData.assigned_roles}
                                            onChange={handleRolesChange}
                                            className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:border-transparent"
                                            size="4"
                                        >
                                            <option value="">All Roles</option>
                                            {availableRoles.map((role) => (
                                                <option key={role.id} value={role.role_name}>
                                                    {role.role_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Editor Footer */}
                    {/* Editor Footer - Step Navigation */}
                    <div className="border-t border-gray-200 bg-gray-50 dark:bg-slate-800/50 px-4 sm:px-6 py-3 sm:py-4">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                            {/* Left side - Status */}
                            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400 justify-center sm:justify-start">
                                {isLastStep ? (
                                    formData.title ? (
                                        <>
                                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                            Ready to publish
                                        </>
                                    ) : (
                                        <>
                                            <AlertCircle className="w-4 h-4 text-amber-500" />
                                            Add a title to publish
                                        </>
                                    )
                                ) : (
                                    <>
                                        <span className="text-gray-400 dark:text-slate-500">Step {currentStepIndex + 1} of {editorSteps.length}</span>
                                    </>
                                )}
                            </div>

                            {/* Right side - Navigation & Actions */}
                            <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 sm:gap-3">
                                {/* Cancel button - always visible */}
                                <button
                                    onClick={handleClose}
                                    className="px-3 sm:px-4 py-2 text-gray-700 hover:bg-gray-100 dark:bg-slate-800 rounded-lg transition-colors text-sm"
                                >
                                    Cancel
                                </button>

                                {/* Back button - visible from step 2 onwards */}
                                {!isFirstStep && (
                                    <button
                                        onClick={handlePrevStep}
                                        className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-100 dark:bg-slate-800 transition-colors text-sm"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                        Back
                                    </button>
                                )}

                                {/* Preview - always visible */}
                                <button
                                    onClick={() => setIsPreviewOpen(true)}
                                    disabled={!formData.title.trim() && (!formData.sections || formData.sections.length === 0)}
                                    className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 border border-indigo-200 dark:border-indigo-500/30 text-indigo-700 bg-indigo-50 dark:bg-indigo-500/10 rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-50 text-sm"
                                    title="Preview how employees will see this task"
                                >
                                    <Eye className="w-4 h-4" />
                                    Preview
                                </button>

                                {/* Save Draft - always visible */}
                                <button
                                    onClick={handleSaveAsDraft}
                                    disabled={isSaving || !formData.title.trim()}
                                    className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-100 dark:bg-slate-800 transition-colors disabled:opacity-50 text-sm"
                                >
                                    <Save className="w-4 h-4" />
                                    <span className="hidden xs:inline">Save </span>Draft
                                </button>

                                {/* Next button - visible on steps 1 & 2 */}
                                {!isLastStep && (
                                    <button
                                        onClick={handleNextStep}
                                        className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors text-sm"
                                    >
                                        Next
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                )}

                                {/* Publish & Schedule buttons - only visible on final step */}
                                {isLastStep && (
                                    <>
                                        <button
                                            onClick={handleSchedule}
                                            disabled={isSaving || !formData.title.trim() || !formData.scheduled_date}
                                            className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm"
                                        >
                                            <Clock className="w-4 h-4" />
                                            Schedule
                                        </button>
                                        <button
                                            onClick={handlePublish}
                                            disabled={isSaving || !formData.title.trim()}
                                            className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-lg transition-colors disabled:opacity-50 shadow-lg shadow-violet-500/25 text-sm"
                                        >
                                            <Send className="w-4 h-4" />
                                            Publish
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Unsaved Changes Modal */}
                {showUnsavedModal && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-xl w-full mx-auto overflow-hidden ring-1 ring-black/5 dark:ring-white/10 transform transition-all scale-100 opacity-100">
                            {/* Header */}
                            <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-8 py-6 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-white dark:bg-slate-900/10 rounded-full -mr-16 -mt-16 blur-xl"></div>
                                <div className="relative flex items-center gap-4">
                                    <div className="w-12 h-12 bg-white dark:bg-slate-900/20 rounded-2xl flex items-center justify-center backdrop-blur-sm shadow-inner">
                                        <AlertCircle className="w-7 h-7 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-white tracking-tight">Unsaved Changes</h3>
                                        <p className="text-amber-100 font-medium text-sm">You have unsaved work in progress</p>
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="px-8 py-8">
                                <p className="text-gray-600 dark:text-slate-300 text-lg leading-relaxed mb-6">
                                    You have modified this task but haven't saved your changes yet. If you leave now, your progress will be lost.
                                </p>

                                {/* Task Preview */}
                                {formData.title && (
                                    <div className="p-5 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm relative overflow-hidden group hover:border-violet-200 transition-colors">
                                        <div className="absolute top-0 right-0 w-24 h-24 bg-gray-100 dark:bg-slate-800 rounded-full -mr-12 -mt-12 opacity-50"></div>
                                        <div className="flex items-start gap-4 relative">
                                            <div className="p-3 bg-indigo-50 dark:bg-indigo-500/10 rounded-xl text-indigo-600 dark:text-indigo-400 shadow-sm group-hover:scale-110 transition-transform duration-300">
                                                {getTaskIcon(formData.task_type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-bold text-gray-900 dark:text-white truncate text-lg pr-4">{formData.title}</h4>
                                                <div className="flex items-center gap-3 mt-1.5">
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold uppercase tracking-wide">
                                                        {formData.task_type}
                                                    </span>
                                                    <span className="text-sm text-gray-500 dark:text-slate-400 font-medium capitalize">
                                                        {formData.phase}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {!formData.title?.trim() && (
                                    <div className="p-4 bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-400 rounded-xl border border-amber-100 dark:border-amber-500/30 text-sm font-medium flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4" />
                                        Please add a title to save this task as a draft.
                                    </div>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="px-8 py-6 bg-gray-50 dark:bg-slate-800/50/80 border-t border-gray-100 dark:border-slate-700/50 flex flex-col-reverse sm:flex-row gap-4">
                                <button
                                    onClick={handleLeaveWithoutSaving}
                                    className="flex-1 px-6 py-3.5 text-gray-600 bg-white dark:bg-slate-900 hover:bg-red-50 dark:bg-red-500/10 hover:text-red-600 dark:text-red-400 hover:border-red-200 border border-gray-200 dark:border-slate-700 rounded-xl font-semibold transition-all duration-200 shadow-sm focus:ring-2 focus:ring-red-100 outline-none"
                                >
                                    Leave Without Saving
                                </button>
                                <button
                                    onClick={handleSaveAsDraftFromModal}
                                    disabled={isSaving || !formData.title?.trim()}
                                    className="flex-[1.5] px-6 py-3.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg shadow-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 transform hover:-translate-y-0.5 focus:ring-2 focus:ring-violet-200 outline-none"
                                >
                                    {isSaving ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            Saving Draft...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-5 h-5" />
                                            Save Changes & Leave
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Unified File Picker Modal */}
            <UnifiedFilePickerModal
                isOpen={showFilePicker}
                onClose={() => setShowFilePicker(false)}
                onAttach={handleFileSelection}
                mode={pickerMode === 'chat' ? 'all' : 'all'}
            />

            {/* Admin Task Preview Modal */}
            <AdminTaskPreviewModal
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                taskData={formData}
            />
        </div >
    );
};

export default AITaskGenerator;

